import { useState } from "react";
import AviationSectorPicker from "../../../components/Sectors/AviationSectorPicker";
import FormSubmitButton from "../../../components/FormSubmitButton";
import SubFlowHeader from "../../../components/GoalSubflow/SubFlowHeader";
import OptionPills from "../../../components/GoalSubflow/OptionPills";
import { useGoalSubflow } from "./useGoalSubflow";
import { COMMUNITY_ROLE_MAX, getOptions } from "../../registry";
import { t } from "../../../../../locales/t";

/**
 * "Career trajectory": the grow-career sub-flow. `planSubFlows` decides which of
 * its questions this member is asked; experience stage is single-choice, as on
 * `ProvideMentorship`.
 */
const GOAL = "grow_career";

const GrowCareer = () => {
  const { asks, index, total, answers, submit } = useGoalSubflow(GOAL);

  const [sectors, setSectors] = useState(
    () => answers.sectors ?? { primary: [], secondary: {} },
  );
  const [communityRole, setCommunityRole] = useState(
    () => answers.communityRole ?? [],
  );
  const [experienceStage, setExperienceStage] = useState(
    () => answers.experienceStage ?? "",
  );
  const [careerGoals, setCareerGoals] = useState(
    () => answers.careerGoals ?? [],
  );
  const [connectionTypes, setConnectionTypes] = useState(
    () => answers.connectionTypes ?? [],
  );

  const canContinue =
    (!asks.sectors || sectors.primary.length > 0) &&
    (!asks.communityRole || communityRole.length > 0) &&
    (!asks.experienceStage || Boolean(experienceStage)) &&
    (!asks.careerGoals || careerGoals.length > 0) &&
    (!asks.connectionTypes || connectionTypes.length > 0);

  const handleContinue = () => {
    if (!canContinue) return;
    submit({
      ...(asks.sectors ? { sectors } : {}),
      ...(asks.communityRole ? { communityRole } : {}),
      ...(asks.experienceStage ? { experienceStage } : {}),
      ...(asks.careerGoals ? { careerGoals } : {}),
      ...(asks.connectionTypes ? { connectionTypes } : {}),
    });
  };

  return (
    <SubFlowHeader
      goal={GOAL}
      index={index}
      total={total}
      onSubmit={handleContinue}
    >
      {asks.sectors && (
        <AviationSectorPicker
          value={sectors}
          onChange={setSectors}
          mode={asks.sectors}
          label={t(`onboarding.subflow.${GOAL}.sectorsLabel`)}
        />
      )}

      {asks.communityRole && (
        <OptionPills
          multiple
          max={COMMUNITY_ROLE_MAX}
          options={getOptions("communityRole")}
          value={communityRole}
          onChange={setCommunityRole}
          label={t("onboarding.subflow.questions.communityRole.label")}
          hint={t("onboarding.subflow.questions.communityRole.hint")}
        />
      )}

      {asks.experienceStage && (
        <OptionPills
          options={getOptions("experienceStage")}
          value={experienceStage}
          onChange={setExperienceStage}
          label={t("onboarding.subflow.questions.experienceStage.label")}
        />
      )}

      {asks.careerGoals && (
        <OptionPills
          multiple
          options={getOptions("careerGoals")}
          value={careerGoals}
          onChange={setCareerGoals}
          label={t("onboarding.subflow.questions.careerGoals.label")}
        />
      )}

      {asks.connectionTypes && (
        <OptionPills
          multiple
          options={getOptions("connectionTypes")}
          value={connectionTypes}
          onChange={setConnectionTypes}
          label={t("onboarding.subflow.questions.connectionTypes.label")}
        />
      )}

      <FormSubmitButton
        variant="contained"
        fullWidth
        disabledReason={!canContinue ? t("onboarding.subflow.incomplete") : ""}
        sx={{ py: 1.5 }}
      >
        {t("onboarding.subflow.continue")}
      </FormSubmitButton>
    </SubFlowHeader>
  );
};

export default GrowCareer;
